import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import api from '../../services/api';
import { useSiteSettings } from '../../context/SiteSettingsContext';

export default function ForgotPassword() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const { settings } = useSiteSettings();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await api.post('/accounts/password-reset/', { email: email.trim() });
      toast.success(res.data?.detail || 'Check your inbox for a reset link.');
      setSent(true);
    } catch (err) {
      if (!err.response) {
        toast.error('Cannot reach the server. Try again in a moment.');
      } else {
        const data = err.response.data;
        const msg = data?.detail
          || (data && typeof data === 'object' ? Object.values(data).flat().join(' ') : null)
          || `Request failed (${err.response.status}).`;
        toast.error(msg);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <StyledWrapper $bgColor={settings?.login_bg_type === 'color' ? (settings?.login_bg_color || '#F8FAFB') : undefined}>
      <div className="container">
        <div className="card">
          <img src={settings?.header_logo || '/NIS ABJ LOGO.jpg'} alt="NIS FCT" className="logo" />
          <span className="title">Forgot Password</span>
          <p className="subtitle">
            Enter the email address linked to your account. We'll send you a
            secure link to reset your password.
          </p>

          {sent ? (
            <div className="sent-box">
              <p>
                If an account with <strong>{email}</strong> exists, a password
                reset link has been sent. Please check your inbox &mdash; and
                your spam folder, just in case &mdash; for an email from
                <em> noreply@nisfct.com.ng</em>.
              </p>
              <p style={{ marginTop: 12 }}>
                The link expires in 24 hours.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="inputBox">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                />
                <span>Email Address</span>
              </div>
              <button className="enter" type="submit" disabled={loading}>
                {loading ? 'Sending...' : 'Send Reset Link'}
              </button>
            </form>
          )}

          <p className="back-link">
            <Link to="/login">&larr; Back to Sign In</Link>
          </p>
        </div>
      </div>
    </StyledWrapper>
  );
}

const StyledWrapper = styled.div`
  .container {
    min-height: 70vh;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 3rem 1rem;
    background: ${(p) => p.$bgColor || '#F8FAFB'};
  }
  .card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 22px;
    width: 420px;
    max-width: 95vw;
    background: #e3e3e3;
    box-shadow: 16px 16px 32px #c8c8c8, -16px -16px 32px #fefefe;
    border-radius: 12px;
    padding: 2.5rem 1.75rem;
  }
  .logo {
    width: auto;
    height: 80px;
    object-fit: contain;
  }
  .title {
    color: #0B5D1E;
    text-transform: uppercase;
    letter-spacing: 2px;
    font-weight: bold;
    font-size: 1.4rem;
    font-family: 'Playfair Display', serif;
  }
  .subtitle {
    color: #4b5563;
    font-size: 0.9rem;
    text-align: center;
    margin: -8px 0 0 0;
    line-height: 1.45;
  }
  form {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 25px;
    width: 100%;
  }
  .inputBox {
    position: relative;
    width: 320px;
    max-width: 100%;
  }
  .inputBox input {
    width: 100%;
    padding: 12px;
    outline: none;
    border: none;
    color: #000;
    font-size: 1em;
    background: transparent;
    border-left: 2px solid #0B5D1E;
    border-bottom: 2px solid #0B5D1E;
    border-bottom-left-radius: 8px;
  }
  .inputBox span {
    margin-top: 5px;
    position: absolute;
    left: 0;
    transform: translateY(-4px);
    margin-left: 10px;
    padding: 10px;
    pointer-events: none;
    font-size: 12px;
    color: #374151;
    text-transform: uppercase;
    transition: 0.5s;
    letter-spacing: 3px;
    border-radius: 8px;
  }
  .inputBox input:valid ~ span,
  .inputBox input:focus ~ span {
    transform: translateX(130px) translateY(-15px);
    font-size: 0.8em;
    padding: 5px 10px;
    background: #0B5D1E;
    letter-spacing: 0.2em;
    color: #fff;
  }
  .inputBox input:valid,
  .inputBox input:focus {
    border: 2px solid #0B5D1E;
    border-radius: 8px;
  }
  .enter {
    height: 48px;
    width: 200px;
    border-radius: 8px;
    border: 2px solid #0B5D1E;
    cursor: pointer;
    background-color: transparent;
    transition: 0.3s;
    text-transform: uppercase;
    font-size: 0.8rem;
    font-weight: 600;
    letter-spacing: 2px;
    color: #0B5D1E;
  }
  .enter:hover { background-color: #0B5D1E; color: white; }
  .enter:disabled { opacity: 0.5; cursor: not-allowed; }
  .sent-box {
    background: #ecfdf5;
    border: 1px solid #0B5D1E;
    color: #064e1a;
    padding: 14px 16px;
    border-radius: 8px;
    font-size: 0.9rem;
    width: 100%;
    line-height: 1.5;
  }
  .back-link {
    font-size: 0.875rem;
  }
  .back-link a {
    color: #0B5D1E;
    text-decoration: none;
    font-weight: 600;
  }
  .back-link a:hover { text-decoration: underline; }
`;
